import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { APIEmptyResponse, ErrorResponse } from "@reflag/shared/api";
import {
  CreateFeatureViewArgsType,
  FeatureViewDTO,
  PatchFeatureViewArgsType,
} from "@reflag/shared/featureViewAPI";

import { featureViewQueryKeys } from "./featureViewQueryKeys";

export function useFeatureViewCreateMutation(
  options: Omit<
    UseMutationOptions<
      FeatureViewDTO,
      AxiosError<ErrorResponse>,
      CreateFeatureViewArgsType
    >,
    "mutationFn"
  > = {},
) {
  const { appId } = useEnvironment();
  const queryClient = useQueryClient();
  const { onSuccess, ...otherOptions } = options;

  return useMutation({
    mutationFn: (data: CreateFeatureViewArgsType) =>
      api
        .post<"/apps/:appId/feature-views">(
          `/apps/${appId}/feature-views`,
          data,
        )
        .then((res) => res.data),
    onSuccess: (createdView, data, context) => {
      queryClient.invalidateQueries({
        queryKey: featureViewQueryKeys.list(appId),
      });
      onSuccess?.(createdView, data, context);
    },
    ...otherOptions,
  });
}

export function useFeatureViewUpdateMutation(
  viewId: string,
  options: Omit<
    UseMutationOptions<
      FeatureViewDTO,
      AxiosError<ErrorResponse>,
      PatchFeatureViewArgsType
    >,
    "mutationFn"
  > = {},
) {
  const { appId } = useEnvironment();
  const queryClient = useQueryClient();
  const { onSuccess, ...otherOptions } = options;

  return useMutation({
    mutationFn: (data: PatchFeatureViewArgsType) =>
      api
        .patch<"/apps/:appId/feature-views/:viewId">(
          `/apps/${appId}/feature-views/${viewId}`,
          data,
        )
        .then((res) => res.data),
    onSuccess: (updatedView, data, context) => {
      queryClient.invalidateQueries({
        queryKey: featureViewQueryKeys.list(appId),
      });
      onSuccess?.(updatedView, data, context);
    },
    ...otherOptions,
  });
}

export function useFeatureViewDeleteMutation(
  viewId: string,
  options: Omit<
    UseMutationOptions<APIEmptyResponse, AxiosError<ErrorResponse>, void>,
    "mutationFn"
  > = {},
) {
  const { appId } = useEnvironment();
  const queryClient = useQueryClient();
  const { onSuccess, ...otherOptions } = options;

  return useMutation({
    mutationFn: () =>
      api
        .delete<"/apps/:appId/feature-views/:viewId">(
          `/apps/${appId}/feature-views/${viewId}`,
        )
        .then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: featureViewQueryKeys.list(appId),
      });

      // Drop all cached data of relations between flags and views
      queryClient.removeQueries({
        queryKey: featureViewQueryKeys.singleFeatureRelationViews(appId),
      });

      onSuccess?.(data, variables, context);
    },
    ...otherOptions,
  });
}
